import { Flex, InputGroup, InputLeftElement, TableContainer, Input, IconButton, Tooltip, Badge } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/table-core";
import { DataTable } from "am-tax-fe-core";
import { ChangeEvent, FunctionComponent, useState, useEffect } from "react";
import { LuSearch } from "react-icons/lu";
import { IconEdit } from "@tabler/icons-react";

import { TableRowProps } from "../../../utils/types";
import { CreateOrEditUserModal } from "./CreateOrEditUserModal";
import { SectionHeader } from "../../../components/SectionHeader";
import { User } from "../../../api";

interface Props {
    users: User[];
    isLoading: boolean;
}

export const UserSection: FunctionComponent<Props> = ({ users, isLoading }) => {
    const [showCreateOrEditClientUserDialog, setShowCreateOrEditClientUserDialog] = useState(false);
    const [editUserData, setEditUserData] = useState<User | undefined>();

    const columnHelper = createColumnHelper<User>();

    const [filteredData, setFilteredData] = useState<User[]>(users);

    // Update filtered data when users changes
    useEffect(() => {
        setFilteredData(users);
    }, [users]);

    function closeCreateOrEditClientUserDialog() {
        setEditUserData(undefined);
        setShowCreateOrEditClientUserDialog(false);
    }

    function enableEditUser(user: User) {
        setEditUserData(user);
        setShowCreateOrEditClientUserDialog(true);
    }

    function handleFilter(event: ChangeEvent<HTMLInputElement>) {
        const value = event.target.value.trim().toLowerCase();
        const filteredList = users.filter(
            c =>
                c.lastName?.toLowerCase().includes(value.toLowerCase()) ||
                c.firstName?.toLowerCase().includes(value.toLowerCase()) ||
                c.email?.toLowerCase().includes(value.toLowerCase()),
        );
        setFilteredData(filteredList);
    }

    return (
        <Flex flexFlow="column nowrap" gap="1rem">
            <SectionHeader title="Users">
                <InputGroup flexGrow="0" flexBasis="35rem" width="35rem">
                    <InputLeftElement pointerEvents="none">
                        <LuSearch />
                    </InputLeftElement>

                    <Input placeholder="Search Users" onChange={handleFilter} />
                </InputGroup>
                {/* <Button leftIcon={<IconPlus />} variant="primary" flexShrink="0" onClick={() => setShowCreateOrEditClientUserDialog(true)}>
                    Add User
                </Button> */}
            </SectionHeader>
            <TableContainer maxHeight="70vh" overflowY="auto" backgroundColor="chakra-body-bg">
                <DataTable
                    enableFilters={false}
                    tableProps={{ size: "sm" }}
                    isLoading={isLoading}
                    noData={isLoading ? undefined : "No users found"}
                    data={filteredData}
                    defaultColumn={{ meta: { tableColumnHeaderProps: { pt: "1rem", zIndex: 1 } } }}
                    columns={[
                        columnHelper.accessor("firstName", {
                            header: "Name",
                            cell: (props: TableRowProps<User>) => {
                                const user = props.row.original;
                                return `${user.firstName || ""} ${user.lastName || ""}`.trim();
                            },
                        }),
                        columnHelper.accessor("email", {
                            header: "Email",
                            cell: (props: TableRowProps<User>) => props.getValue(),
                        }),
                        columnHelper.accessor("isSystemAdmin", {
                            header: "System Admin",
                            cell: (props: TableRowProps<User>) => {
                                const isSystemAdmin = props.getValue();
                                return (
                                    <Badge colorScheme={isSystemAdmin ? "green" : "gray"} variant="subtle">
                                        {isSystemAdmin ? "Yes" : "No"}
                                    </Badge>
                                );
                            },
                        }),
                        {
                            id: "Edit User",
                            header: "Actions",
                            cell: props => {
                                const row = props.row.original;
                                return (
                                    <Tooltip label="Edit System Admin Status">
                                        <IconButton
                                            icon={<IconEdit />}
                                            aria-label="Edit System Admin Status"
                                            variant="outline"
                                            size="sm"
                                            onClick={evt => {
                                                evt.stopPropagation();
                                                enableEditUser(row);
                                            }}
                                        />
                                    </Tooltip>
                                );
                            },
                        },
                    ]}
                />
            </TableContainer>
            <Flex>
                <CreateOrEditUserModal
                    show={showCreateOrEditClientUserDialog}
                    onClose={closeCreateOrEditClientUserDialog}
                    clientUserData={editUserData}
                    existingClientUsers={filteredData}
                />
            </Flex>
        </Flex>
    );
};
